import React from 'react';
import {
  EvernoteIntegration, UploadIntegrationType, ExternalFileContents,
} from 'hellospa/page/prep-and-send/data/types/integration';
import CustomPicker from './custom-picker';
import ReAuthModal from './reauth-modal';
import evernoteIcon from 'images/external_logo_med_E.png';
import { useLatestRef } from 'hellospa/common/hooks';

type Props = {
  settings: EvernoteIntegration,
  shouldTriggerPicker: boolean,
  onSuccess: (id: string, name: string) => void
  toggleIntegrationPicker: (integration: string, status: boolean) => void,
}

export default function EvernoteFilePicker({
  settings,
  shouldTriggerPicker,
  onSuccess,
  toggleIntegrationPicker,
}: Props) {

  const onClose = React.useCallback(() => {
    toggleIntegrationPicker(UploadIntegrationType.Evernote, false);
  }, [toggleIntegrationPicker]);

  // NOTE: The useEffect needs to be stable, meaning it only runs the effect
  // when `settings.enabled` or `shouldTriggerPicker` change. onSuccess is not
  // stable on its own unless the parent wraps it in `useCallback`. By using a
  // ref here the callback can continue to be updated without triggering
  // useEffect. If you want to see the bug this fixes, add `onSuccess` to the
  // dependency array.  If this re-renders while the picker is open, it'll
  // flicker beause it'll open a new picker.
  const successRef = useLatestRef(onSuccess);
  const onFileSelect = React.useCallback((file: ExternalFileContents) => () => {
    successRef.current(file.id, file.name);
    // toggle the modal
    onClose();
  }, [onClose, successRef]);

  // if clicked on the integration icon but it's not enabled,
  // trigger the modal to initiate auth
  if (shouldTriggerPicker && !settings.enabled) {
    return (
      <ReAuthModal
        integration={UploadIntegrationType.Evernote}
        integrationIcon={evernoteIcon}
        isOpen={shouldTriggerPicker && !settings.enabled}
        onClose={onClose} />
    );
  }

  // return null if it hasn't been triggered or enabled
  if (!shouldTriggerPicker || !settings.enabled) {
    return null;
  }

  return (
    <CustomPicker
      integration={UploadIntegrationType.Evernote}
      isOpen={shouldTriggerPicker && settings.enabled}
      integrationIcon={evernoteIcon}
      onFileSelect={onFileSelect}
      onClose={onClose} />
  );
}
